import React from "react";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption, getBaseUnits, getUnitOptions } from "utils/fields";
import { getDefaultComboSpecs, getDhwComboEfDefault, getDhwTemplate } from "utils/mechanicals";
import { maxLength, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { createKeyboardShortcut } from "utils/keyboardShortcuts";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import DwhrSystem from "features/Model/Mechanicals/HotWater/DwhrSystem";
import Tooltip from "components/Tooltip";

import classes from "./style.module.scss";

import Add from "assets/images/icons/JSX/Add";

const charLim100 = maxLength(100);
const comboEnerSourceValidation = getValidation("comboEnergySource");
const comboEquipTypeValidation = getValidation("comboEquipType");

const comboCapacityValidation = getValidation("comboOutputCapacity");

const comboSizingFactorValidation = getValidation("comboSizingFactor");

const comboEffValidation = getValidation("comboEff");

const comboPilotValidation = getValidation("comboPilotLight");
const comboFlueValidation = getValidation("comboFlueDiameter");

const tankLocationValidation = getValidation("comboTankLocation");
const tankVolumeValidation = getValidation("comboTankVolume");
const comboEFValidation = getValidation("comboEF");
const circPumpValidation = getValidation("comboCircPumpPower");

export default ({ accessor, change, ...props }) => {
    const dispatch = useDispatch();

    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const isDwhr = !isEmpty(
        useStateSelector(`model.modelData.domesticHotWater.primarySystem.drainWaterHeatRecovery`) || {}
    );

    const { isParametricAnalysis = false } = props;

    const { id: energySourceId } = useStateSelector(`${accessor}.system.combo.energySource`) || {};
    const { id: equipTypeId } = useStateSelector(`${accessor}.system.combo.equipmentType`) || {};
    const { id: capacityId, value: capacityValue } =
        useStateSelector(`${accessor}.system.combo.specifications.outputCapacity`) || {};
    const effValue = useStateSelector(`${accessor}.system.combo.specifications.efficiency`);
    const pilotLightValue = useStateSelector(`${accessor}.system.combo.specifications.pilotLight`);
    const flueDiameterValue = useStateSelector(`${accessor}.system.combo.specifications.flueDiameter`);
    const isEfDefaults = useStateSelector(`${accessor}.system.combo.tankAndPump.energyFactor.useDefaults`);
    const efValue = useStateSelector(`${accessor}.system.combo.tankAndPump.energyFactor.value`);

    const { id: tankVolumeId, value: tankVolumeValue } =
        useStateSelector(`${accessor}.system.combo.tankAndPump.tankCapacity`) || {};
    const isCircPumpCalculated =
        useStateSelector(`${accessor}.system.combo.tankAndPump.circulationPump.isCalculated`) || {};

    const capacityUnits = useStateSelector(`${accessor}.system.combo.specifications.outputCapacity_u`);
    const pilotUnits = useStateSelector(`${accessor}.system.combo.specifications.pilotLight_u`);
    const flueUnits = useStateSelector(`${accessor}.system.combo.specifications.flueDiameter_u`);
    const tankVolumeUnits = useStateSelector(`${accessor}.system.combo.tankAndPump.tankCapacity_u`);
    const circPumpPowerUnits = useStateSelector(`${accessor}.system.combo.tankAndPump.circulationPump_u`);

    const handleCreateDwhr = () => {
        change(`model.modelData.domesticHotWater.primarySystem.hasDrainWaterHeatRecovery`, true);
        change(`model.modelData.domesticHotWater.primarySystem.drainWaterHeatRecovery`, getDhwTemplate("dwhr"));
    };

    const handleDeleteDwhr = () => {
        change(`model.modelData.domesticHotWater.primarySystem.hasDrainWaterHeatRecovery`, false);
        change(`model.modelData.domesticHotWater.primarySystem.drainWaterHeatRecovery`, {});
        dispatch(removeFormErrors(`model.modelData.domesticHotWater.primarySystem.drainWaterHeatRecovery`));
        dispatch(removeFormErrors(`model.modelData.domesticHotWater.primarySystem.connectedUnitsDwhr`));
    };

    return (
        <>
            {isParametricAnalysis && (
                <div
                    style={{
                        backgroundColor: "#fbe9dc",
                        borderRadius: "10px",
                        padding: "0.75rem",
                        lineHeight: "normal",
                        fontSize: "small",
                        marginTop: "-1.5rem",
                        marginBottom: "1.25rem",
                    }}
                >
                    If both a Combo system and a DHW system are present in the same parametric analysis data point, the
                    DHW system (including the cost) will be ignored.
                </div>
            )}
            <div className={classes.heading}>
                <div
                    className={`${classes.addDHWR} ${isDwhr && classes.disabled}`}
                    onClick={() => handleCreateDwhr()}
                    data-tip="Add Drain Water Heat Recovery"
                    data-for="dwhrHeader"
                    {...createKeyboardShortcut({ onKeyDown: handleCreateDwhr })}
                >
                    Add DWHR <Add />
                </div>
                <Tooltip id="dwhrHeader" />
            </div>

            <InputRow gridTemplate="23.4% 23.4% 10rem 23.4%">
                <Field
                    component={Input}
                    name={`${accessor}.system.combo.manufacturer`}
                    type="text"
                    label="Manufacturer Name"
                    placeholder="Enter Manufacturer Name"
                    customValidateFunc={charLim100}
                />
                <Field
                    component={Input}
                    name={`${accessor}.system.combo.model`}
                    type="text"
                    label="Model Name"
                    placeholder="Enter Model Name"
                    customValidateFunc={charLim100}
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.system.combo.energyStar`}
                    label="Energy Star"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
            </InputRow>
            <InputRow gridTemplate="23.4% 23.4% 10rem 23.4%">
                <Field
                    component={Select}
                    name={`${accessor}.system.combo.energySource`}
                    options={getOptions({ fieldKey: `comboEnergySource` })}
                    label="Energy Source"
                    placeholder="Choose Energy Source"
                    validate={comboEnerSourceValidation}
                    onChange={({ id }) => {
                        const firstOption = getFirstOption({ indKey: id, depKey: `comboEquipType` });
                        change(`${accessor}.system.combo.equipmentType`, firstOption);

                        const {
                            efficiency = 80,
                            pilotLight = 0,
                            flueDiameter = 0,
                        } = getDefaultComboSpecs(firstOption.id) || {};
                        change(`${accessor}.system.combo.specifications.efficiency`, efficiency);
                        change(`${accessor}.system.combo.specifications.pilotLight`, pilotLight);
                        change(`${accessor}.system.combo.specifications.flueDiameter`, flueDiameter);
                    }}
                    info={
                        "An electric combo system should be modelled as separate electric boiler and an electric DHW systems."
                    }
                />
                <Field
                    component={Select}
                    name={`${accessor}.system.combo.equipmentType`}
                    options={getOptions({ fieldKey: `comboEquipType`, indKey: energySourceId })}
                    label="Equipment Type"
                    placeholder="Choose Equipment Type"
                    validate={comboEquipTypeValidation}
                    onChange={({ id }) => {
                        const { efficiency = 80, pilotLight = 0, flueDiameter = 0 } = getDefaultComboSpecs(id) || {};
                        change(`${accessor}.system.combo.specifications.efficiency`, efficiency);
                        change(`${accessor}.system.combo.specifications.pilotLight`, pilotLight);
                        change(`${accessor}.system.combo.specifications.flueDiameter`, flueDiameter);

                        if (isEfDefaults) {
                            change(
                                `${accessor}.system.combo.tankAndPump.energyFactor.value`,
                                getDhwComboEfDefault({ equipTypeId: id, tankVolId: tankVolumeId })
                            );
                        }
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.system.combo.specifications.outputCapacity`}
                    options={[
                        {
                            label: "User Specified",
                            value: {
                                id: 0,
                                value: capacityValue,
                                uiUnits: "kW",
                            },
                        },
                        {
                            label: "Calculated",
                            value: {
                                id: 1,
                                value: capacityValue,
                                uiUnits: "kW",
                            },
                        },
                    ]}
                    label="Capacity Type"
                    placeholder="Choose Input Type"
                    validate={comboCapacityValidation}
                    onChange={({ id }) => {
                        if (id === 1) {
                            change(`${accessor}.fansAndPumps.power.isCalculated`, true);
                        }
                    }}
                    info={
                        "If the capacity is unknown, set field to Calculated. Note that the calculated value will not appear in Volta SNAP, but will be updated prior to simulating in HOT2000."
                    }
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.combo.specifications.outputCapacity.value`}
                    label="Output Capacity"
                    placeholder="0.00"
                    validate={comboCapacityValidation}
                    decimals={getDecimalPlaces(`comboOutputCapacity`)}
                    change={change}
                    disabled={capacityId === 1}
                    units={{
                        base: getBaseUnits(`comboOutputCapacity`, modelUnits),
                        options: getUnitOptions("power"),
                        selected: capacityUnits,
                        unitType: "power",
                        accessor: `${accessor}.system.combo.specifications.outputCapacity_u`,
                    }}
                />
                <Field
                    component={Input}
                    type="number"
                    name={`${accessor}.system.combo.specifications.sizingFactor`}
                    label="Sizing Factor"
                    placeholder={0.0}
                    validate={comboSizingFactorValidation}
                    decimals={getDecimalPlaces(`comboSizingFactor`)}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.combo.specifications.efficiency`}
                    label="Efficiency"
                    placeholder="0.00"
                    validate={comboEffValidation}
                    decimals={getDecimalPlaces(`comboEff`)}
                    change={change}
                    setValue={effValue}
                    units={{
                        base: getBaseUnits(`comboEff`, modelUnits),
                        options: ["%"],
                        selected: "%",
                        unitType: "fraction",
                        accessor: `${accessor}.system.combo.specifications.efficiency_u`,
                    }}
                    info={
                        "Unless modelling a boiler combo system, leave at default value. Note that the Combined Seasonal Efficienct (CA-AFUE) is not accepted in Canada."
                    }
                />
                <Field
                    component={Select}
                    name={`${accessor}.system.combo.specifications.isSteadyState`}
                    options={[
                        {
                            label: "Steady State",
                            value: true,
                        },
                        {
                            label: "AFUE",
                            value: false,
                        },
                    ]}
                    label="Efficiency Type"
                    placeholder="Choose Efficiency Type"
                    info={
                        "For condensing systems, always use the AFUE rating. For non-condensing systems, use AFUE whenever possible."
                    }
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.combo.specifications.pilotLight`}
                    label="Pilot Light"
                    placeholder="0.00"
                    validate={comboPilotValidation}
                    decimals={getDecimalPlaces(`comboPilotLight`)}
                    change={change}
                    disabled={false}
                    setValue={pilotLightValue}
                    units={{
                        base: getBaseUnits(`comboPilotLight`, modelUnits),
                        options: getUnitOptions("dailyEnergy"),
                        selected: pilotUnits,
                        unitType: "dailyEnergy",
                        accessor: `${accessor}.system.combo.specifications.pilotLight_u`,
                    }}
                    info={"Leave at the default value (variable based on system)."}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.combo.specifications.flueDiameter`}
                    label="Flue Diameter"
                    placeholder="0.00"
                    validate={comboFlueValidation}
                    decimals={getDecimalPlaces(`comboFlueDiameter`)}
                    change={change}
                    disabled={false}
                    setValue={flueDiameterValue}
                    units={{
                        base: getBaseUnits(`comboFlueDiameter`, modelUnits),
                        options: getUnitOptions("length"),
                        selected: flueUnits,
                        unitType: "length",
                        accessor: `${accessor}.system.combo.specifications.flueDiameter_u`,
                    }}
                    info={"Leave at the default value (variable based on system)."}
                />
            </InputRow>
            <h4 style={{ marginBottom: "1.25rem", marginTop: "1.75rem" }}>Combo Tank & Pump</h4>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.system.combo.tankAndPump.tankCapacity`}
                    options={[
                        {
                            label: "User specified",
                            value: {
                                id: 0,
                                value: tankVolumeValue,
                            },
                        },
                        ...getOptions({ fieldKey: "comboTankVolume" }),
                    ]}
                    label="Tank Volume"
                    placeholder="Choose Tank Volume"
                    validate={tankVolumeValidation}
                    onChange={({ id }) => {
                        change("model.modelData.domesticHotWater.primarySystem.tankVolume.id", id);
                        if (isEfDefaults && [1, 2, 3, 4, 5].includes(id)) {
                            change(
                                `${accessor}.system.combo.tankAndPump.energyFactor.value`,
                                getDhwComboEfDefault({ equipTypeId: equipTypeId, tankVolId: id })
                            );
                        }
                    }}
                    info={"For a tankless water heater, input a user specified value of 0."}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.combo.tankAndPump.tankCapacity.value`}
                    label="Volume"
                    placeholder={0.0}
                    validate={tankVolumeValidation}
                    setTouched={true}
                    decimals={getDecimalPlaces("comboTankVolume")}
                    change={change}
                    setValue={tankVolumeValue}
                    disabled={tankVolumeId !== 0}
                    units={{
                        base: getBaseUnits("comboTankVolume", modelUnits),
                        options: getUnitOptions("volume"),
                        selected: tankVolumeUnits,
                        unitType: "volume",
                        accessor: `${accessor}.system.combo.tankAndPump.tankCapacity_u`,
                    }}
                    onChange={(value) => {
                        change("model.modelData.domesticHotWater.primarySystem.tankVolume.value", value);
                    }}
                />
                <Field
                    component={Select}
                    name={`${accessor}.system.combo.tankAndPump.tankLocation`}
                    options={getOptions({ fieldKey: "comboTankLocation" })}
                    label="Tank Location"
                    placeholder="Choose Tank Location"
                    validate={tankLocationValidation}
                    onChange={({ id }) => {
                        change("model.modelData.domesticHotWater.primarySystem.tankLocation", { id });
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="23.25% 23.25% 15% 15% 15%">
                <Field
                    component={Select}
                    name={`${accessor}.system.combo.tankAndPump.energyFactor.useDefaults`}
                    options={[
                        {
                            label: "User specified",
                            value: false,
                        },
                        {
                            label: "Use Defaults",
                            value: true,
                        },
                    ]}
                    label="Energy Factor Input Type"
                    placeholder="Choose Input Type"
                    onChange={(value) => {
                        if (value) {
                            change("model.modelData.domesticHotWater.primarySystem.energyFactor.id", 0);
                        } else {
                            change("model.modelData.domesticHotWater.primarySystem.energyFactor.id", 1);
                        }
                    }}
                />
                <Field
                    component={Input}
                    name={`${accessor}.system.combo.tankAndPump.energyFactor.value`}
                    type="number"
                    label="Energy Factor"
                    placeholder={0.0}
                    setValue={efValue}
                    validate={comboEFValidation}
                    disabled={isEfDefaults}
                    onChange={(value) => {
                        change("model.modelData.domesticHotWater.primarySystem.energyFactor.value", value);
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.system.combo.tankAndPump.circulationPump.isCalculated`}
                    options={[
                        {
                            label: "User specified",
                            value: false,
                        },
                        {
                            label: "Calculated",
                            value: true,
                        },
                    ]}
                    label="Circulation Pump Power Type"
                    placeholder="Choose Input Type"
                />
                <Field
                    component={Input}
                    name={`${accessor}.system.combo.tankAndPump.circulationPump.value`}
                    type="number"
                    label="Circulation Pump Power"
                    placeholder={0.0}
                    validate={circPumpValidation}
                    setTouched={true}
                    decimals={getDecimalPlaces("comboCircPumpPower")}
                    change={change}
                    disabled={isCircPumpCalculated}
                    units={{
                        base: getBaseUnits("comboCircPumpPower", modelUnits),
                        options: getUnitOptions("power"),
                        selected: circPumpPowerUnits,
                        unitType: "power",
                        accessor: `${accessor}.system.combo.tankAndPump.circulationPump_u`,
                    }}
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.system.combo.tankAndPump.circulationPump.energyEfficientMotor`}
                    label="Energy Efficient Pump Motor"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
            </InputRow>
            {isDwhr && (
                <DwhrSystem
                    accessor={`model.modelData.domesticHotWater.primarySystem.drainWaterHeatRecovery`}
                    change={change}
                    handleDelete={() => handleDeleteDwhr()}
                />
            )}
        </>
    );
};
